/**
 * EmailCallbackPage - Handles OAuth callback after Gmail authorization
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { connectEmailAccount } from '../../../api/admin';

export default function EmailCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // Prevent double-execution from React 18 Strict Mode
    if (hasRun.current) return;
    hasRun.current = true;

    const code = searchParams.get('code');
    const stateFromUrl = searchParams.get('state');
    const storedState = localStorage.getItem('email_oauth_state');

    if (!code) {
      setError('No authorization code received. Please try connecting your email again.');
      return;
    }

    if (!stateFromUrl || !storedState || stateFromUrl !== storedState) {
      setError('Invalid OAuth state. Please try connecting your email again.');
      return;
    }

    // Parse role from OAuth state (JSON: { nonce, role })
    let role = 'system';
    try {
      const parsed = JSON.parse(storedState);
      if (parsed?.role) role = parsed.role;
    } catch {
      // Legacy state format (plain UUID) — default to 'system'
    }

    async function connect() {
      try {
        await connectEmailAccount(code!, stateFromUrl!, role);
        localStorage.removeItem('email_oauth_state');
        navigate('/admin/settings?email_connected=true', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to connect email account');
      }
    }

    connect();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-danger-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-warm-900 mb-2">Connection Failed</h2>
          <p className="text-sm text-warm-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/settings')}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
          <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-warm-900 mb-2">Connecting your email account...</h2>
        <p className="text-sm text-warm-500">Please wait while we complete the setup.</p>
      </div>
    </div>
  );
}
